import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedIcon } from '../../components/icons';
import { adminService } from '../../services/adminService';
import { CommissionReport, CommissionSummary } from '../../types';

const CSS = `
.zs-stat-card {
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: var(--sc-radius-md);
  border: 1px solid var(--sc-border);
  background: var(--sc-bg-secondary);
  box-shadow: var(--sc-shadow-sm);
  padding: 12px 14px;
  text-decoration: none;
  display: block;
  transition: all var(--sc-transition-base);
}
.zs-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--sc-shadow-md);
}
.zs-stat-card-circle {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  transition: transform var(--sc-transition-slow);
}
.zs-stat-card:hover .zs-stat-card-circle {
  transform: scale(1.15);
}
.zs-stat-card-iconbox {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform var(--sc-transition-base);
}
.zs-stat-card:hover .zs-stat-card-iconbox {
  transform: scale(1.06) rotate(-3deg);
}
`;

export const AdminDeals: React.FC = () => {
  const [reports, setReports] = useState<CommissionReport[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = { date_from: dateFrom || undefined, date_to: dateTo || undefined };
      const [rep, sum] = await Promise.all([
        adminService.getCommissionReports(params),
        adminService.getCommissionSummary({ period: 'monthly' }),
      ]);
      setReports(rep.results ?? []);
      setSummary(sum);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <style>{CSS}</style>
      <h1 style={s.title}>Confirmed Deals & Commissions</h1>

      {summary && (
        <div style={s.summaryRow}>
          {[
            { label: 'Total Deals', val: summary.total_deals, icon: 'clipboard', color: 'var(--sc-brand-primary)' },
            { label: 'Deal Val', val: `$${(summary.total_deal_value ?? 0).toFixed(2)}`, icon: 'money', color: 'var(--sc-success)' },
            { label: 'Expected Comm', val: `$${(summary.total_expected_commission ?? 0).toFixed(2)}`, icon: 'money', color: 'var(--sc-info-text)' },
          ].map((k) => (
            <div key={k.label} className="zs-stat-card">
              {/* Top color bar */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg, ${k.color}, ${k.color}88)`
              }} />
              {/* Decorative circle */}
              <div className="zs-stat-card-circle" style={{ backgroundColor: `var(--sc-bg-active)` }} />
              
              {/* Content inner */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, gap: 4, textAlign: 'center' }}>
                <div className="zs-stat-card-iconbox" style={{ background: `var(--sc-bg-hover)`, color: k.color, border: `1px solid var(--sc-border)`, margin: '0 auto' }}>
                  <AnimatedIcon name={k.icon as any} size={16} color={k.color} />
                </div>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 1 }}>
                  {k.label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sc-text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {k.val}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Date filter */}
      <div style={s.filterRow}>
        <label style={s.filterLabel}>From: <input type="date" style={s.dateInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></label>
        <label style={s.filterLabel}>To: <input type="date" style={s.dateInput} value={dateTo} onChange={e => setDateTo(e.target.value)} /></label>
        <button style={s.filterBtn} onClick={loadData}>Apply Filter</button>
      </div>

      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : reports.length === 0 ? (
        <div style={s.empty}>No commission reports found.</div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 16, boxShadow: 'var(--sc-shadow-sm)', border: '1px solid var(--sc-border)' }}>
          <div style={{ ...s.table, border: 'none', boxShadow: 'none' }}>
            <div style={s.thead}>
              {[{ label: 'ID', flex: 0.8 }, { label: 'Client', flex: 1.5 }, { label: 'Surveyor', flex: 1.5 }, { label: 'Value', flex: 1.2 }, { label: 'Rate', flex: 0.8 }, { label: 'Comm.', flex: 1.2 }, { label: 'Confirmed', flex: 1.2 }].map(h => (
                <div key={h.label} style={{ ...s.th, flex: h.flex }}>{h.label}</div>
              ))}
            </div>
            {reports.map((r) => (
              <div key={r.deal_id} style={s.row} className="zs-tr">
                <div style={{ ...s.td, flex: 0.8, color: 'var(--sc-text-tertiary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.deal_id?.slice(0, 8)}</div>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.client_name}</div>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.surveyor_name}</div>
                <div style={{ ...s.td, flex: 1.2, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>${(r.deal_value ?? 0).toFixed(2)}</div>
                <div style={{ ...s.td, flex: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{((r.commission_rate ?? 0) * 100).toFixed(0)}%</div>
                <div style={{ ...s.td, flex: 1.2, fontWeight: 700, color: 'var(--sc-success)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>${(r.expected_commission ?? 0).toFixed(2)}</div>
                <div style={{ ...s.td, flex: 1.2, fontSize: 12, color: 'var(--sc-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{new Date(r.confirmed_at).toLocaleDateString('en-ZW')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By-Surveyor Aggregation */}
      {summary && summary.by_surveyor && summary.by_surveyor.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2 style={s.sectionTitle}>Commission by Surveyor</h2>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 16, boxShadow: 'var(--sc-shadow-sm)', border: '1px solid var(--sc-border)' }}>
            <div style={{ ...s.table, border: 'none', boxShadow: 'none' }}>
              <div style={s.thead}>
                {[{ label: 'Surveyor', flex: 2 }, { label: 'Deals', flex: 1, align: 'center' as const }, { label: 'Total Value', flex: 1.5 }, { label: 'Comm.', flex: 1.5 }, { label: 'Avg/Deal', flex: 1.5 }].map(h => (
                  <div key={h.label} style={{ ...s.th, flex: h.flex, textAlign: h.align }}>{h.label}</div>
                ))}
              </div>
              {[...summary.by_surveyor]
                .sort((a, b) => b.total_commission - a.total_commission)
                .map((sv) => (
              <div key={sv.surveyor_id} style={s.row}>
                <div style={{ ...s.td, flex: 2, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sv.surveyor_name}</div>
                <div style={{ ...s.td, flex: 1, textAlign: 'center' as const }}>
                  <span style={s.badge}>{sv.deals}</span>
                </div>
                <div style={{ ...s.td, flex: 1.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>${(sv.total_value ?? 0).toFixed(2)}</div>
                <div style={{ ...s.td, flex: 1.5, fontWeight: 700, color: 'var(--sc-success)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>${(sv.total_commission ?? 0).toFixed(2)}</div>
                <div style={{ ...s.td, flex: 1.5, color: 'var(--sc-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sv.deals > 0 ? `$${((sv.total_commission ?? 0) / sv.deals).toFixed(2)}` : '—'}
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 20px', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 },
  summCard: { display: 'block', background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '20px 22px', border: '1px solid var(--sc-border)', textAlign: 'center' as const, boxShadow: 'var(--sc-shadow-card)' },
  kpiIcon: { width: 44, height: 44, borderRadius: '50%', background: 'var(--sc-bg-primary)', border: '1px solid var(--sc-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' },
  summVal: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  summLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },
  filterRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  filterLabel: { fontSize: 13, color: 'var(--sc-text-secondary)', display: 'flex', alignItems: 'center', gap: 8 },
  dateInput: { padding: '8px 12px', border: '1px solid var(--sc-border)', borderRadius: 8, fontSize: 13, marginLeft: 4, color: 'var(--sc-text-primary)', background: 'var(--sc-bg-secondary)' },
  filterBtn: { padding: '8px 16px', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13, transition: 'all 0.2s' },
  table: { background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-md)', minWidth: 600 },
  thead: { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  row: { display: 'flex', padding: '13px 20px', borderBottom: '1px solid var(--sc-divider)', alignItems: 'center' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 16, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 14px', fontFamily: "'Outfit', sans-serif" },
  badge: { display: 'inline-block', padding: '2px 10px', background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', borderRadius: 20, fontWeight: 700, fontSize: 13 },
};
